import { useTranslation } from 'next-i18next';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSearchContext } from '../search-new/context/SearchContext';
import Slider from '../../new-components/Slider';
import { styled } from 'styled-components';
import DropdownInput from '../home/components/search-bar/DropdownInput';
import SortByContainer from '../search/components/search-filters/sort-by-input/SortByContainer';
import { useSorting } from '../search-new/hooks/Sorting.hooks';
import NawyNowFilterIcon from '../../new-components/svg-icons/NawyNowFilterIcon';
import ExplorePropertiesContainer from './components/explore-properties/ExploreNawyNowContainer';
import WebAppJourney from '../home/components/web-app-journey/WebAppJourney';
import NawyNowFinancing from './components/NawyNowFinancing';
import MapContainer from '../map/components/MapContainer';
import HeroSection from './components/HeroSection';
import Header from './components/Header';
import IntroSection from './components/IntroSection';
import StepsSection from './components/StepsSection';
import { useDispatch, useSelector } from 'react-redux';
import { useLocationFilter } from '../search/hooks/LocationFilter';
import { useRouter } from 'next/router';
import { setSelectedCompound } from '../../redux/slices/mapSlice';
import { SearchFiltersActionTypes } from '../../reducers/search-filters/actions';
import { setCompounds } from '../../redux/slices/nawyNowSlice';
import { SearchFiltersTypes } from '../../reducers/search-filters/reducer';
import SearchBar from '../search-new/components/search-bar/SearchBar';

const NawyNowView = (props: any) => {
  const { t } = useTranslation('nawy-now');
  const { sorting } = useSorting();
  const {
    searchFiltersState,
    setShowFilters,
    appliedFiltersCount,
    searchFiltersDispatch
  } = useSearchContext();

  const {
    areas: areasFilters,
    compounds: compoundsFilters,
    developers: developersFilters
  } = searchFiltersState.searchFilters;

  const nawyNowAppliedFiltersCount =
    appliedFiltersCount -
    (areasFilters.length ? 1 : 0) -
    (compoundsFilters.length ? 1 : 0) -
    (developersFilters.length ? 1 : 0);

  const formRef = useRef<HTMLDivElement | null>(null);
  const propertiesRef = useRef<HTMLDivElement | null>(null);
  const { locale } = useRouter();
  const dispatch = useDispatch();

  const initialFilterOptions = {
    areas: {
      name: 'areas',
      options: props.areas,
      selectedValues: []
    },
    compounds: {
      name: 'compounds',
      options: props.compounds.map((compound: any) => ({
        ...compound,
        value: compound.id
      })),
      selectedValues: []
    },
    developers: {
      name: 'developers',
      options: props.developers,
      selectedValues: []
    }
  };
  const [filters, setFilters] = useState<any>(initialFilterOptions);

  const { handleSelect } = useLocationFilter();
  const { areas, compounds, developers } = useSelector(
    (state: any) => state.nawyNow
  );

  useEffect(() => {
    dispatch(setCompounds([]));
    searchFiltersDispatch({
      type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
      payload: {
        name: SearchFiltersTypes.COMPOUNDS,
        value: []
      }
    });
    dispatch(setSelectedCompound(null));
  }, []);

  useEffect(() => {
    setFilters({
      ...initialFilterOptions,
      areas: {
        ...initialFilterOptions.areas,
        selectedValues: areas
      },
      compounds: {
        ...initialFilterOptions.compounds,
        selectedValues: compounds
      },
      developers: {
        ...initialFilterOptions.developers,
        selectedValues: developers
      }
    });
  }, [areas, compounds, developers]);

  const handleClick = (
    e: ChangeEvent<HTMLInputElement>,
    filterType: string
  ) => {
    const { value, name } = e.target;
    const id = Number(value);

    if (filterType === 'areas') {
      handleSelect({ name: name, id: id, category: 'area' });
    }

    if (filterType === 'compounds') {
      handleSelect({ name: name, id: id, category: 'compound' });
    }

    if (filterType === 'developers') {
      handleSelect({ name: name, id: id, category: 'developer' });
    }
  };

  return (
    <Container>
      <Header animate={props?.animate} />
      <HeroSection
        developerLogos={props?.developerLogos}
        animate={props?.animate}
        propertiesRef={propertiesRef}
      />
      <IntroSection />
      <StepsSection propertiesRef={propertiesRef} formRef={formRef} />

      <div className=" listings-section container-fluid">
        {props.sliderProps?.children?.length > 0 && (
          <div className="dont-miss-section">
            <div className="slider-header">{t('dont_miss')}</div>
            <Slider {...props?.sliderProps} />
          </div>
        )}
        <Slider {...props?.developersSliderProps} />
        <div ref={propertiesRef}>
          <div className="properties-header">
            <span className="header-text">
              {t('find_home')}{' '}
              {locale == 'en' && (
                <span className="header-text" style={{ color: '#ff5e00' }}>
                  {t('now')}
                </span>
              )}
            </span>
            <div className="search-container">
              <SearchBar searchBarLocation="Nawy_now" />
              <div className="filters-section">
                <div className="dropdown-inputs">
                  <DropdownInput
                    filter={filters.areas}
                    handleSelect={handleClick}
                  />
                  <DropdownInput
                    filter={filters.compounds}
                    handleSelect={handleClick}
                  />
                  <DropdownInput
                    filter={filters.developers}
                    handleSelect={handleClick}
                  />
                </div>

                <div className="search-actions">
                  <FilterBtn onClick={() => setShowFilters(true)}>
                    {t('filter', { ns: 'entity' })}
                    <NawyNowFilterIcon />
                    {nawyNowAppliedFiltersCount &&
                    nawyNowAppliedFiltersCount > 0 ? (
                      <span className="filters-indicator">
                        {nawyNowAppliedFiltersCount}
                      </span>
                    ) : null}
                  </FilterBtn>

                  <SortByContainer
                    sortByOptions={sorting}
                    searchFiltersState={searchFiltersState}
                    searchFiltersDispatch={searchFiltersDispatch}
                    sortLocation={'nawy_now'}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="properties-map">
            <ExplorePropertiesContainer
              exploreLocation="nawy_now_page"
              filterOptions={props?.filterOptions}
              initialSearchResults={props?.loadedSearchResultsSSR}
            />
            <MapContainer />
          </div>
        </div>

        <NawyNowFinancing
          areas={props.areas}
          propertyTypes={props.propertyTypes}
          compounds={props.compounds}
          formRef={formRef}
        />

        <WebAppJourney />
      </div>
    </Container>
  );
};

const Container = styled.div<any>`
  display: flex;
  flex-direction: column;
  padding: 40px 0;

  ul {
    position: relative;
    z-index: 10;
  }

  .listings-section {
    display: flex;
    flex-direction: column;
    gap: 70px;
    margin-top: 70px;
  }

  .dont-miss-section {
    display: flex;
    flex-direction: column;
    gap: 24px;
    .slider-header {
      color: #2c3138;
      font-family: materialBold;
      font-size: 32px;
      text-transform: capitalize;
    }
  }

  .properties-header {
    display: flex;
    flex-direction: column;
    gap: 40px;
    .header-text {
      color: #2c3138;
      text-align: center;
      font-family: materialBold;
      font-size: 36px;
      text-transform: capitalize;
    }

    .search-container {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }

    .search-dropdown {
      width: 650px !important;
    }
  }

  .map {
    max-width: 33%;
    height: 708px !important;
    border-radius: 18px;
    overflow: hidden;
    box-shadow:
      0px 0px 4px 0px rgba(0, 0, 0, 0.04),
      0px 4px 8px 0px rgba(0, 0, 0, 0.06);

    .map-expand-button {
      position: absolute;
      top: 10px;
      right: 15px;
      width: 30px;
      height: 30px;
      display: flex;
      padding: 5px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.5);
      border: 0px solid transparent;
    }
  }

  .properties-map {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 24px;

    .cards-wrapper {
      min-width: 67%;

      .properties-grid {
        grid-template-columns: repeat(2, minmax(360px, 1fr));
      }
    }

    .pagination-container {
      padding-bottom: 0;
    }

    .map {
      margin-top: 72px;
      position: sticky;
      top: 100px;
      max-height: calc(100vh - 120px);
    }
  }
  .filters-section {
    display: flex;
    width: 100%;
    justify-content: space-between;
    gap: 12px;
    .dropdown-inputs {
      display: flex;
      gap: 24px;
      > div {
        width: 262px;
        height: 48px;
        border-radius: 12px;
        border: 0.5px solid rgba(71, 71, 71, 0.21);
        background: #fff;
        span {
          color: #2c3138;
          text-align: center;
          font-family: materialRegular;
          font-size: 14px;
        }
        div {
          max-height: 300px;
          z-index: 100;
        }
      }
    }

    .search-actions {
      display: flex;
      align-items: center;
      width: auto;
      gap: 16px;
      > div {
        height: 36px;
      }
      button {
        height: 36px;
        background-color: #fff;
        color: #015c9a;
        font-family: materialRegular;
        font-size: 16px;
        padding: 6px 16px;
        border-radius: 12px;
        border: 1px solid #015c9a;
      }
    }
  }
`;

const FilterBtn = styled.button`
  background-color: #015c9a;
  outline: none;
  border: none;
  padding: 6px 14px;
  border-radius: 8px;
  color: #fff;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-family: materialMedium;
  line-height: 150%;
  cursor: pointer;
  svg {
    width: 20px;
    height: 20px;
  }

  .filters-indicator {
    font-size: 10px !important;
    color: ${props => props.theme.light.colors.white} !important;
    background-color: ${props => props.theme.light.colors.primaryOrange};
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }
`;

export default NawyNowView;
